import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LoadOrgStatus } from '@/modules/auth/use-cases'
import type { OrgStatus } from '@/shared/domain'
import { ACCOUNT_LOAD_RETRY_DELAY_MS, canUseCommandCenter, retryOnce } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import { useAuth } from '@/shared/ui/hooks'

const LOAD_FALLBACK_MESSAGE = "We couldn't load your account. Check your connection and try again."

export type UseNoAccessPageProps = {
  onLoadOrgStatus: LoadOrgStatus
}

export type NoAccessView = 'loading' | 'error' | 'pageRestricted' | 'noCommandCenterAccess'

export type UseNoAccessPageResult = {
  view: NoAccessView
  errorMessage: string
  identity?: string
  activeOrgNames: string[]
  invitationOrgNames: string[]
  handleRetry: () => void
  handleUseDifferentAccount: () => Promise<void>
  handleRegister: () => void
  handleGoToDashboard: () => void
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; orgStatus: OrgStatus }

export function useNoAccessPage({ onLoadOrgStatus }: UseNoAccessPageProps): UseNoAccessPageResult {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' })
  const requestIdRef = useRef(0)

  const load = useCallback(() => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setLoadState({ status: 'loading' })
    retryOnce(onLoadOrgStatus, ACCOUNT_LOAD_RETRY_DELAY_MS)
      .then((orgStatus) => {
        if (requestIdRef.current !== requestId) return
        if (!canUseCommandCenter(orgStatus) && orgStatus.pendingOwnerInvitations.length === 1) {
          navigate(`/join/${orgStatus.pendingOwnerInvitations[0].token}`, { replace: true })
          return
        }
        setLoadState({ status: 'ready', orgStatus })
      })
      .catch((err: unknown) => {
        if (requestIdRef.current !== requestId) return
        setLoadState({
          status: 'error',
          message: reportError(err, { fallbackMessage: LOAD_FALLBACK_MESSAGE }),
        })
      })
  }, [navigate, onLoadOrgStatus])

  useEffect(() => {
    load()
    return () => {
      requestIdRef.current += 1
    }
  }, [load])

  const handleUseDifferentAccount = useCallback(async () => {
    await signOut()
    navigate('/sign-in', { replace: true })
  }, [navigate, signOut])

  const handleRegister = useCallback(() => {
    navigate('/register')
  }, [navigate])

  const handleGoToDashboard = useCallback(() => {
    navigate('/dashboard', { replace: true })
  }, [navigate])

  const orgStatus = loadState.status === 'ready' ? loadState.orgStatus : null
  const view: NoAccessView =
    loadState.status === 'loading'
      ? 'loading'
      : loadState.status === 'error'
        ? 'error'
        : orgStatus && canUseCommandCenter(orgStatus)
          ? 'pageRestricted'
          : 'noCommandCenterAccess'

  return {
    view,
    errorMessage: loadState.status === 'error' ? loadState.message : '',
    identity: user?.email || undefined,
    activeOrgNames: orgStatus?.activeOrgs.map((org) => org.name) ?? [],
    invitationOrgNames: orgStatus?.pendingMemberInvitations.map((invite) => invite.orgName) ?? [],
    handleRetry: load,
    handleUseDifferentAccount,
    handleRegister,
    handleGoToDashboard,
  }
}
